import { useCallback } from 'react'

import { useUserPreferencesQuery, useUserPreferencesMutation } from './api/useUserPreferences'

/**
 * Collapse or expand the setup checklist for THIS user (VRG-340).
 *
 * REPLACED `useDismissHome`, which set a permanent `homeDismissed` flag. Dismissal was the
 * wrong shape for a page that no longer retires: an unfinishable checklist — a solo operator
 * will never "invite a teammate" — would otherwise nag forever from the top of a page the user
 * cannot leave, so an escape hatch is genuinely needed. But dismissal destroyed the block, and
 * making that survivable needed a confirmation modal AND a restore affordance elsewhere in the
 * product: three pieces of machinery to undo the damage of the first one.
 *
 * Collapsing needs none of them. The header and the count stay, expanding is one click, and no
 * confirmation is warranted because nothing is lost — a confirm is for effects that are
 * downstream or hard to reverse, and needing one here would have been the tell that the action
 * was wrong rather than that it needed guarding.
 *
 * Per-user and server-side, so it follows the person across devices and one admin tidying
 * their own Home does not collapse a new hire's.
 */
export const useCollapseChecklist = () => {
  const { preferences } = useUserPreferencesQuery()
  const { update, isSaving } = useUserPreferencesMutation()

  const isCollapsed =
    preferences.homeChecklistCollapsed === true || preferences.homeDismissed === true

  const toggle = useCallback(async () => {
    await update({
      homeChecklistCollapsed: !isCollapsed,
      // Clear the legacy flag on the way through, so a user who dismissed under the old model
      // can actually expand again — otherwise it would keep forcing collapsed forever.
      ...(preferences.homeDismissed ? { homeDismissed: false } : {}),
    })
  }, [update, isCollapsed, preferences.homeDismissed])

  return { isCollapsed, toggle, isToggling: isSaving }
}
